/**
 * Copyright (c) 2024 Securisea, Inc.
 * All rights reserved.
 *
 * This is unpublished, propriety, internal
 * source code of Securisea, Inc.
 * No license is provided to any third parties.
 * If found outside of a Securisea, Inc. environment
 * report to bounty@securisea.com for a possible cash reward
 * and promptly delete all files.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_TableOptions,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
  useMaterialReactTable,
} from "material-react-table";

import { Box, Button, IconButton, Tooltip, Chip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  useGetOrganizations,
  useUpdateOrganization,
  useDeleteOrganization,
  useCreateOrganization,
} from "../../../hooks/organizations";
import { Organization } from "../../../types/organizations";
import { SlackNotificationManager } from "../../notifications/slack";
import { getUserInfo } from "../../../helpers/user";
import AdminEditOrganizationModal from "../../organizations/edit";

const OrganizationTable = () => {
  const queryClient = useQueryClient();
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null);
  const user = getUserInfo();
  const [globalFilter, setGlobalFilter] = useState<string | undefined>("");
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    setPagination({
      pageIndex: 0,
      pageSize: 10,
    });
  }, [columnFilters]);

  const { mutateAsync: deleteOrganization } = useDeleteOrganization();

  const {
    data: organizationQueryResults = [],
    isError: isLoadingOrganizationsError,
    isFetching: isFetchingOrganizations,
    isLoading: isLoadingOrganizations,
  } = useGetOrganizations({ pagination, columnFilters, sorting, globalFilter });

  const fetchedOrganizations = organizationQueryResults[0]?.organizations || [];

  useEffect(() => {
    const count = organizationQueryResults[0]?.rowCount;
    if (count == 0) {
      setRowCount(0);
    }
    if (!count || count === rowCount) {
      return;
    }
    setRowCount(count);
  }, [organizationQueryResults]);

  const handleSaveOrganization: MRT_TableOptions<Organization>["onEditingRowSave"] =
    async ({ values, table }) => {
      const newValidationErrors = validateOrganization(values);
      if (Object.values(newValidationErrors).some((error) => error)) {
        setValidationErrors(newValidationErrors);
        return;
      }
      setValidationErrors({});
      await updateOrganization(values);
    };

  const handleCreateOrganization: MRT_TableOptions<Organization>["onCreatingRowSave"] =
    async ({ values, table }) => {
      const newValidationErrors = validateOrganization(values);
      if (Object.values(newValidationErrors).some((error) => error)) {
        setValidationErrors(newValidationErrors);
        return;
      }
      setValidationErrors({});
      await createOrganization(values);
    };

  const openDeleteConfirmModal = (row: MRT_Row<Organization>) => {
    if (window.confirm("Are you sure you want to delete this Organization?")) {
      deleteOrganization(row.original.id);
    }
  };

  const handleEditOrganization = (organization: Organization) => {
    const organizationData: Organization = {
      id: organization.id || "",
      name: organization.name || "",
      short_description: organization.short_description || "",
      description: organization.description || "",
      user_count: organization.user_count || "",
      slack_webhook_url: organization.slack_webhook_url || "",
      enforce_2fa: organization.enforce_2fa || false,
      slack_enabled: organization.slack_enabled || false,
      org_admins_can_manage_er_users: organization.org_admins_can_manage_er_users || false,
    };
    setSelectedOrganization(organizationData);
    setEditModalOpen(true);
  };

  const handleCreateNewOrganization = () => {
    setSelectedOrganization(null);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedOrganization(null);
    queryClient.invalidateQueries({ queryKey: ["organizations"] });
  };

  function validateOrganization(organization: Organization) {
    const validateRequired = (value: any) => !!value;

    return {
      name: !validateRequired(organization.name) ? "Name is required" : "",
    };
  }

  const tableName = "organizations_table";
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const tableState = JSON.parse(localStorage.getItem("tableState") || "{}");
    return (
      tableState[tableName]?.columnVisibility || {
        id: false,
      }
    );
  });

  useEffect(() => {
    localStorage.setItem(
      "tableState",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("tableState") || "{}"),
        [tableName]: {
          columnVisibility,
        },
      })
    );
  }, [columnVisibility, tableName]);

  const columns = useMemo<MRT_ColumnDef<Organization>[]>(
    () => [
      {
        accessorKey: "id",
        header: "Id",
        enableEditing: false,
        enableSorting: false,
        size: 80,
      },

      {
        accessorKey: "name",
        header: "Name",
        enableEditing: (row) => !row.original.id,
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.name,
          helperText: validationErrors?.name,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              name: undefined,
            }),
        },
      },
      {
        accessorKey: "short_description",
        header: "Short Description",
        enableColumnFilter: false,
        enableSorting: false,
        muiEditTextFieldProps: {
          type: "text",
        },
      },
      {
        accessorKey: "description",
        header: "Description",
        enableSorting: false,
        enableColumnFilter: false,
        muiEditTextFieldProps: {
          type: "text",
        },
      },
      {
        accessorKey: "user_count",
        header: "User Count",
        enableSorting: false,
        enableEditing: false,
        enableColumnFilter: false,
        muiEditTextFieldProps: {
          type: "text",
        },
      },
      {
        accessorKey: "enforce_2fa",
        header: "Enforce 2FA",
        enableSorting: false,
        enableEditing: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Chip
            label={row.original.enforce_2fa ? "Yes" : "No"}
            color={row.original.enforce_2fa ? "success" : "default"}
            size="small"
          />
        ),
      },
      {
        accessorKey: "slack_enabled",
        header: "Slack Enabled",
        enableSorting: false,
        enableEditing: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Chip
            label={row.original.slack_enabled ? "Yes" : "No"}
            color={row.original.slack_enabled ? "success" : "default"}
            size="small"
          />
        ),
      },
      {
        accessorKey: "org_admins_can_manage_er_users",
        header: "Org Admins Can Manage ER Users",
        enableSorting: false,
        enableEditing: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Chip
            label={row.original.org_admins_can_manage_er_users ? "Yes" : "No"}
            color={row.original.org_admins_can_manage_er_users ? "success" : "default"}
            size="small"
          />
        ),
      },
      {
        accessorKey: "actions",
        header: "Actions",
        enableEditing: false,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <SlackNotificationManager organization_id={row.id} />
        ),
      },
    ],
    [validationErrors]
  );

  const table = useMaterialReactTable({
    columns,
    enableGlobalFilter: false,
    data: fetchedOrganizations,
    initialState: {
      showColumnFilters: false,
      density: "compact",
      columnVisibility: columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    createDisplayMode: "modal",
    editDisplayMode: "modal",
    selectAllMode: "all",
    enableColumnResizing: true,
    getRowId: (row) => row.id,
    rowNumberDisplayMode: "original",
    muiToolbarAlertBannerProps: isLoadingOrganizationsError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount,
    muiTableContainerProps: {
      sx: {
        minHeight: "500px",
      },
    },
    renderBottomToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Button variant="contained" onClick={handleCreateNewOrganization}>
          New Organization
        </Button>
        <SlackNotificationManager organization_id={user.organization_id} />
      </Box>
    ),
    onCreatingRowCancel: () => setValidationErrors({}),
    onEditingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateOrganization,
    onEditingRowSave: handleSaveOrganization,

    enableRowActions: true,
    renderRowActionMenuItems: ({ row, table }) => [
      <Box key={row.id}>
        <Tooltip title="Edit">
          <IconButton onClick={() => handleEditOrganization(row.original)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        {/* <Tooltip title="Delete">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip> */}
      </Box>,
    ],
    state: {
      columnFilters,
      globalFilter,
      pagination,
      sorting,
      columnVisibility: columnVisibility,
      isLoading: isLoadingOrganizations,
      showAlertBanner: isLoadingOrganizationsError,
      showProgressBars: isFetchingOrganizations,
    },
  });

  const { mutateAsync: updateOrganization } = useUpdateOrganization({ table });
  const { mutateAsync: createOrganization } = useCreateOrganization({ table });

  return (
    <>
      <MaterialReactTable table={table} />
      <AdminEditOrganizationModal
        key={selectedOrganization?.id || "new"}
        organization={selectedOrganization}
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
      />
    </>
  );
};

export default OrganizationTable;
